import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './CitationRenderer.css';

interface Citation {
  number: number;
  title: string;
  url: string;
}

interface CitationRendererProps {
  content: string;
}

// Helper function to extract text from React children
const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === 'string') {
    return children;
  }
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('');
  }
  if (React.isValidElement(children) && children.props.children) {
    return extractTextFromChildren(children.props.children);
  }
  if (typeof children === 'number') {
    return String(children);
  }
  return '';
};

const CitationRenderer: React.FC<CitationRendererProps> = ({ content }) => {
  // Parse citations from content
  const parseCitations = (text: string): { mainContent: string; citations: Citation[] } => {
    const citations: Citation[] = [];
    let mainContent = text;
    
    // Look for the "Sources:" section
    const sourcesIndex = text.indexOf('\nSources:\n');
    if (sourcesIndex !== -1) {
      mainContent = text.substring(0, sourcesIndex).trim();
      const sourcesSection = text.substring(sourcesIndex + '\nSources:\n'.length);
      
      // Parse each citation line
      const citationLines = sourcesSection.split('\n').filter(line => line.trim());
      citationLines.forEach(line => {
        const match = line.match(/^\[(\d+)\]\s+(.+?)\s+-\s+(.+)$/);
        if (match) {
          citations.push({
            number: parseInt(match[1]),
            title: match[2],
            url: match[3]
          });
        }
      });
    }
    
    return { mainContent, citations };
  };
  
  const { mainContent, citations } = parseCitations(content);
  
  // Custom renderer for inline citations
  const renderWithCitations = (text: string) => {
    // Replace [1], [2], etc. with superscript links
    const citationPattern = /\[(\d+)\]/g;
    const parts = text.split(citationPattern);
    
    return parts.map((part, index) => {
      if (index % 2 === 0) {
        // Regular text
        return part;
      } else {
        // Citation number
        const citationNum = parseInt(part);
        return (
          <sup key={`citation-${index}`} className="citation-link">
            <a href={`#citation-${citationNum}`}>[{citationNum}]</a>
          </sup>
        );
      }
    });
  };
  
  return (
    <div className="citation-content">
      <div className="main-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => (
              <p>{renderWithCitations(extractTextFromChildren(children))}</p>
            ),
            li: ({ children }) => (
              <li>{renderWithCitations(extractTextFromChildren(children))}</li>
            ),
            table: ({ children }) => (
              <div className="table-wrapper">
                <table className="research-table">{children}</table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="research-table-head">{children}</thead>
            ),
            tbody: ({ children }) => (
              <tbody className="research-table-body">{children}</tbody>
            ),
            tr: ({ children }) => (
              <tr className="research-table-row">{children}</tr>
            ),
            th: ({ children }) => (
              <th className="research-table-header">{children}</th>
            ),
            td: ({ children }) => (
              <td className="research-table-cell">{renderWithCitations(extractTextFromChildren(children))}</td>
            ),
          }}
        >
          {mainContent}
        </ReactMarkdown>
      </div>
      
      {citations.length > 0 && (
        <div className="citations-section">
          <h4 className="citations-header">Sources</h4>
          <ol className="citations-list">
            {citations.map(citation => (
              <li key={citation.number} id={`citation-${citation.number}`} className="citation-item">
                <span className="citation-number">[{citation.number}]</span>
                <span className="citation-title">{citation.title}</span>
                {citation.url && (
                  <a 
                    href={citation.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="citation-url"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M6.543 7.457L1 1.914 1.914 1l5.543 5.543L7.457 6.543zM10.5 1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V2.707L5.354 7.354a.5.5 0 1 1-.708-.708L9.293 2H6.5a.5.5 0 0 1 0-1h4z"/>
                      <path d="M14 4.5V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4.5a.5.5 0 0 0-1 0V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4.5a.5.5 0 0 0-1 0z"/>
                    </svg>
                  </a>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default CitationRenderer;